import { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { ArrowLeft, Plus, Trash2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function TimeTracker() {
  const navigate = useNavigate();
  const [entries, setEntries] = useState([]);
  const [samples, setSamples] = useState([]);
  const [open, setOpen] = useState(false);
  const [formData, setFormData] = useState({
    sample_id: '',
    sample_number: '',
    employee_id: '',
    employee_name: '',
    task_description: '',
    hours_spent: 0
  });

  useEffect(() => {
    fetchEntries();
    fetchSamples();
  }, []);

  const fetchSamples = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/samples`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSamples(response.data);
    } catch (error) {
      console.error('Failed to fetch samples');
    }
  };

  const fetchEntries = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/time-tracker`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEntries(response.data);
    } catch (error) {
      toast.error('Failed to fetch time entries');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      const payload = { ...formData, hours_spent: parseFloat(formData.hours_spent) };
      await axios.post(`${API}/time-tracker`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Time entry created');
      setOpen(false);
      setFormData({ sample_id: '', sample_number: '', employee_id: '', employee_name: '', task_description: '', hours_spent: 0 });
      fetchEntries();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Operation failed');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure?')) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/time-tracker/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Deleted successfully');
      fetchEntries();
    } catch (error) {
      toast.error('Delete failed');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      <header className="bg-white shadow-md border-b mb-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex justify-between items-center">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate(-1)} data-testid="back-to-dashboard">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-2xl font-bold" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Sampling Time Tracker</h1>
              <p className="text-sm text-gray-600">Log time spent on samples</p>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="flex justify-end mb-6">
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button onClick={() => setFormData({ sample_id: '', sample_number: '', employee_id: '', employee_name: '', task_description: '', hours_spent: 0 })} data-testid="add-time-btn">
                <Plus className="w-4 h-4 mr-2" /> Log Time
              </Button>
            </DialogTrigger>
            <DialogContent data-testid="time-dialog">
              <DialogHeader>
                <DialogTitle>Log Time Entry</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label>Sample</Label>
                  <select className="w-full px-3 py-2 border rounded-md" value={formData.sample_id}
                    onChange={(e) => {
                      const sample = samples.find(s => s.id === e.target.value);
                      setFormData({ ...formData, sample_id: e.target.value, sample_number: sample?.sample_number || '' });
                    }} required data-testid="sample-select">
                    <option value="">Select Sample</option>
                    {samples.map(s => <option key={s.id} value={s.id}>{s.sample_number} - {s.article_name}</option>)}
                  </select>
                </div>
                <div className="space-y-2">
                  <Label>Employee Name</Label>
                  <Input value={formData.employee_name} onChange={(e) => setFormData({ ...formData, employee_name: e.target.value, employee_id: e.target.value })} required data-testid="employee-name-input" />
                </div>
                <div className="space-y-2">
                  <Label>Task Description</Label>
                  <textarea className="w-full px-3 py-2 border rounded-md" rows="3" value={formData.task_description} onChange={(e) => setFormData({ ...formData, task_description: e.target.value })} required data-testid="task-input" />
                </div>
                <div className="space-y-2">
                  <Label>Hours Spent</Label>
                  <Input type="number" step="0.5" value={formData.hours_spent} onChange={(e) => setFormData({ ...formData, hours_spent: e.target.value })} required data-testid="hours-input" />
                </div>
                <Button type="submit" className="w-full" data-testid="submit-time">Log Time</Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        <div className="bg-white rounded-lg shadow overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Sample #</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Employee</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Task</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Hours</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {entries.map((entry) => (
                <tr key={entry.id} data-testid={`time-row-${entry.id}`}>
                  <td className="px-6 py-4">{entry.sample_number}</td>
                  <td className="px-6 py-4">{entry.employee_name}</td>
                  <td className="px-6 py-4">{entry.task_description}</td>
                  <td className="px-6 py-4">{entry.hours_spent}h</td>
                  <td className="px-6 py-4">
                    <Button variant="ghost" size="icon" onClick={() => handleDelete(entry.id)} data-testid={`delete-time-${entry.id}`}>
                      <Trash2 className="w-4 h-4 text-red-500" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {entries.length === 0 && <div className="text-center py-12" data-testid="no-time-entries"><p className="text-gray-500">No time entries found</p></div>}
      </main>
    </div>
  );
}
